import {makeAutoObservable} from "mobx";
import {equals} from "../../utils";
import {AddressDto, WarehouseDetailDto} from "../../api/sls";
import {validationStore} from "../ValidationStore";
import {api, catchApi} from "../../api";
import {pageLoadingStore} from "../PageLoadingStore";
import {SlsWarehouseAddressFormData} from "../../components/page/sls/SlsWarehouseAddressForm";

class SlsWarehouseDetailStore {
    get addresses(): SlsWarehouseAddressFormData[] {
        return this._addresses;
    }

    set addresses(value: SlsWarehouseAddressFormData[]) {
        this._addresses = value;
    }

    private get data(): WarehouseDetailDto | undefined {
        return this._data;
    }

    private set data(value: WarehouseDetailDto | undefined) {
        this._data = value;
    }

    get id(): string | undefined {
        return this._id;
    }

    get warehouseNumber(): string | undefined {
        return this._warehouseNumber;
    }

    set warehouseNumber(value: string | undefined) {
        this._warehouseNumber = value;
    }

    get name(): string | undefined {
        return this._name;
    }

    set name(value: string | undefined) {
        this._name = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    private set valid(value: boolean) {
        this._valid = value;
    }

    get creating(): boolean {
        return this._saving;
    }

    private set saving(value: boolean) {
        this._saving = value;
    }

    get dirty(): boolean {
        const original = this.data
        const current = this.build()
        return !equals(original, current)
    }

    set id(value: string | undefined) {
        this._id = value;
        this.load().finally()
    }

    private _id: string | undefined
    private _name: string | undefined
    private _warehouseNumber: string | undefined
    private _addresses: SlsWarehouseAddressFormData[] = []
    private _data: WarehouseDetailDto | undefined
    private _saving: boolean = false
    private _valid: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    reset() {
        const {data} = this
        this.warehouseNumber = data?.data.warehouseNumber
        this.name = data?.data.name
        this.addresses = data?.data.internalAddresses?.map(ia => this.toFormAddress(ia)) || []
    }

    deleteAddress(position: number){
        const toDelete = [...this.addresses]
        toDelete.splice(position,1)
        this.addresses = toDelete
        this.validate().finally()
    }

    async load() {
        const {
            id
        } = this
        if (!id) {
            return
        }
        pageLoadingStore.loading = true
        const result = await catchApi(async () => api.sls.api.getWarehouse(id))
        this.data = result?.data
        if(this.data && this.data.data.internalAddresses){
            this.addresses = this.data.data.internalAddresses.map(ia => this.toFormAddress(ia));
        }
        pageLoadingStore.loading = false
        this.reset()
    }

    async save(): Promise<WarehouseDetailDto | undefined> {
        const {id} = this
        if (!id) {
            return undefined
        }
        this.saving = true
        const dto = this.build()
        const result = await catchApi(async () => await api.sls.api.updateWarehouse(id, dto))
        const data = result?.data
        this.data = data
        this.reset()
        this.saving = false
        return data
    }

    async validate() {
        this.valid = false
        const originalNumber = this.data?.data.warehouseNumber
        const warehouseNumber = this.warehouseNumber
        const dto = this.build()
        await validationStore.validateWithQuery(api.sls.api.validateWarehouse, dto.data, {
            validateWarehouseNumber: originalNumber !== warehouseNumber
        })

        this.valid = Boolean(validationStore.valid)
    }

    private build(): WarehouseDetailDto {
        return {
            audit: this.data?.audit,
            data: {
                name: this.name || '',
                warehouseNumber: this.warehouseNumber || '',
                internalAddresses: this.addresses.map(a => this.toAddressDto(a))
            }
        }
    }

    private toAddressDto(address: SlsWarehouseAddressFormData): AddressDto {
        return {
            city: address.city,
            country: address.country,
            houseNumber: address.houseNumber || undefined,
            postCode: address.postCode,
            street: address.street
        }
    }

    private toFormAddress(address: AddressDto): SlsWarehouseAddressFormData {
        return {
            city: address.city,
            country: address.country,
            houseNumber: address.houseNumber || '',
            postCode: address.postCode,
            street: address.street
        }
    }
}

const slsWarehouseDetailStore = new SlsWarehouseDetailStore()

export {
    slsWarehouseDetailStore
}
